import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { useTheme } from "styled-components";

import { emailIcon, passwordIcon, phoneIcon } from "UI/FnrInput2/defaultIcons";

import { colorIsLight } from "Utilities/Colors/colorUtils";
import useLastOrDefault from "Utilities/Hooks/useLastOrDefault";
import { useLocalStorage } from "Utilities/Hooks/useLocalStorage";
import useUrlParams from "Utilities/Hooks/useUrlParams";
import externalNavigate from "Utilities/externalNavigate";

import { use2FAState, useOnSuccessPlus } from "./API/queryHooks";
import { useTwoFAContext } from "./TwoFA";
import { componentKeys } from "./components/screens/componentsConfig";
import {
  USE_LOGIN_PASSWORD,
  variants,
  welcomeMessageComponentKeys,
} from "./constants";

/**
 * This allows the backend to populate data that the front end that may manipulate and sent back.
 */
const useInitLocalState = () => {
  const { networkID, setNetworkID, firstFactor, setFirstFactor } =
    useTwoFAContext();
  const [{ networkID: networkIDApi, firstFactor: firstFactorApi }] =
    use2FAState();

  useEffect(() => {
    // networkID may be known by the backend already and needs to be sent with each request to confirm the user still wants that network.
    if (!networkID && networkIDApi) setNetworkID(networkIDApi);
  }, [networkID, networkIDApi, setNetworkID]);

  useEffect(() => {
    // first factor may be known by the backend already and needs to be sent with each request to confirm the user still wants to log in what that account.
    if (!firstFactor && firstFactorApi) setFirstFactor(firstFactorApi);
  }, [firstFactor, firstFactorApi, setFirstFactor, setNetworkID]);
};

const usePostData = () => {
  const { redirect, forceLoginPage } = useUrlParams();
  const { fundID } = useParams();

  const {
    firstFactor,
    firstCode,
    secondCode,
    networkID,
    password,
    usePassword,
    tabID,
  } = useTwoFAContext();

  return useMemo(
    () =>
      Object.fromEntries(
        Object.entries({
          firstFactor,
          firstCode: !usePassword && firstCode,
          secondCode,
          networkID,
          password: usePassword && password,
          tabID,
          fundID: Number(fundID),
          forceLoginPage,
          redirect,
        }).filter(([key, value]) => Boolean(value))
      ),
    [
      firstCode,
      firstFactor,
      networkID,
      password,
      secondCode,
      redirect,
      forceLoginPage,
      tabID,
      fundID,
      usePassword,
    ]
  );
};

/**
 * The payload needed to clear the user's session so they can start over.
 */
const useStartOverPayload = () => {
  const postData = usePostData();
  return {
    ...postData,
    firstFactor: null,
    firstCode: null,
    secondCode: null,
    networkID: null,
    password: null,
  };
};

const useClearNetworkPayload = () => {
  const postData = usePostData();
  return {
    ...postData,
    networkID: null,
  };
};

const useRequestAuthCodePayload = () => {
  // Omit any auth codes when requesting a new one.
  const { firstCode, secondCode, ...postData } = usePostData();
  return {
    ...postData,
    requestAuthCode: true,
  };
};

const useClearNetworkIDPayload = () => {
  const postData = usePostData();

  return useMemo(() => ({ ...postData, networkID: null }), [postData]);
};

const useActiveComponentKey = () => {
  const [
    {
      firstFactor,
      networks,
      firstFactorSuccess,
      secondFactorSuccess,
      allowPasswordForFirstFactor,
      needUserName,
      loginSuccess,
      pending,
    },
  ] = use2FAState();
  const { usePassword, successSplash } = useTwoFAContext();
  const runSuccess = useRunSuccessWithoutSplash();
  const [lastKey, setLastKey] = useState();

  // This logic goes with redirect screen ommission, thus is kept here.
  useEffect(() => {
    if (loginSuccess && !needUserName) runSuccess();
  }, [loginSuccess, needUserName, runSuccess]);

  const activeComponentKey = useMemo(() => {
    if (pending) return componentKeys.Pending;
    if (loginSuccess && !needUserName) {
      if (successSplash) return componentKeys.Redirect;
      return lastKey;
    }
    if (needUserName) return componentKeys.FullName;
    if (networks && networks?.length > 0) return componentKeys.NetworkSelect;
    if (!firstFactor) return componentKeys.FirstFactor;
    if (!firstFactorSuccess && allowPasswordForFirstFactor && usePassword)
      return componentKeys.FirstFactorPassword;
    if (!firstFactorSuccess) return componentKeys.FirstFactorCode;
    if (!secondFactorSuccess) return componentKeys.SecondFactorCode;
  }, [
    pending,
    loginSuccess,
    needUserName,
    networks,
    firstFactor,
    firstFactorSuccess,
    allowPasswordForFirstFactor,
    usePassword,
    secondFactorSuccess,
    successSplash,
    lastKey,
  ]);

  // lastKey is used to freeze the component when loginSuccess is true.
  if (lastKey !== activeComponentKey) {
    setLastKey(activeComponentKey);
  }

  return activeComponentKey;
};

const typeToIcon = { email: emailIcon, phone: phoneIcon };

const useFirstFactorIcon = () => {
  const [{ firstFactorType }] = use2FAState();

  return useLastOrDefault(typeToIcon[firstFactorType], passwordIcon);
};

const useSecondFactorIcon = () => {
  const [{ secondFactorType }] = use2FAState();

  return useLastOrDefault(typeToIcon[secondFactorType], passwordIcon);
};

const useIsLegacy = () => {
  const { variant } = useTwoFAContext();
  return variant !== variants.login;
};

const useIsDark = () => {
  const { isDark } = useTwoFAContext();
  return isDark;
};

const useAccentColor = () => {
  const { accentColor } = useTwoFAContext();
  const isLegacy = useIsLegacy();
  const {
    colors: { fnDarkBlue: oldFnBlue },
    palette: { fnBlue },
  } = useTheme();

  return accentColor || (isLegacy ? oldFnBlue : fnBlue);
};

const useButtonTextColor = () => {
  const accentColor = useAccentColor();
  const {
    palette: {
      text: { textBlack, textLight },
    },
  } = useTheme();

  if (colorIsLight(accentColor)) return textBlack;
  return textLight;
};

const useUsePassword = () => {
  const { usePassword, setUsePassword: setStateUsePassword } =
    useTwoFAContext();
  const [{ allowPasswordForFirstFactor, firstFactor }] = use2FAState();
  const [, setStoredUsePassword] = useLocalStorage(USE_LOGIN_PASSWORD);
  const setUsePassword = (value) => {
    setStateUsePassword(value);
    setStoredUsePassword(value);
  };

  useEffect(() => {
    if (!allowPasswordForFirstFactor && firstFactor) setUsePassword(false);
  });

  return [usePassword, setUsePassword];
};

const useActiveWelcomeMessageKey = () => {
  const [{ userFullName }] = use2FAState();
  const { headerText } = useTwoFAContext();
  return useMemo(() => {
    if (headerText) return welcomeMessageComponentKeys.withCustomText;
    if (userFullName) return welcomeMessageComponentKeys.withName;
    return welcomeMessageComponentKeys.withoutName;
  }, [userFullName, headerText]);
};

const useRunSuccessWithoutSplash = () => {
  const { successSplash, noRedirect } = useTwoFAContext();
  const [{ redirectUrl }] = use2FAState();
  const onSuccess = useOnSuccessPlus();
  const [hasRan, setHasRan] = useState(false);

  const runSuccessWithoutSplash = useCallback(() => {
    if (!successSplash && !hasRan) {
      if (redirectUrl && !noRedirect) externalNavigate(redirectUrl, "_self");
      if (onSuccess) onSuccess();
    }
    setHasRan(true);
  }, [onSuccess, redirectUrl, successSplash, hasRan, noRedirect]);

  return runSuccessWithoutSplash;
};

export {
  usePostData,
  useActiveComponentKey,
  useStartOverPayload,
  useClearNetworkIDPayload,
  useRequestAuthCodePayload,
  useClearNetworkPayload,
  useInitLocalState,
  useFirstFactorIcon,
  useSecondFactorIcon,
  useIsLegacy,
  useAccentColor,
  useButtonTextColor,
  useIsDark,
  useUsePassword,
  useActiveWelcomeMessageKey,
  useRunSuccessWithoutSplash,
};
